import {
  BRAND,
  describeRequest,
  LIMITS,
  PHONE_ERROR_MESSAGES,
  parseIsraeliPhone,
  REGISTRANT_TYPE_LABELS,
  validateRegistration,
  whatsappShareUrl,
  type ChatMessageView,
  type PersonalViewResponse,
  type PublicAnnouncement,
  type PublicJobInfo,
  type RegistrantType,
} from '@crew/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PublicJobCard } from '@/components/contractor/PublicJobCard';
import { ChatView, type ChatLine } from '@/components/chat/ChatView';
import { ChatIcon, CheckIcon, CopyIcon, LinkIcon, MegaphoneIcon, WhatsAppIcon } from '@/components/icons';
import { ContractorLayout } from '@/components/layout/ContractorLayout';
import { Button } from '@/components/ui/Button';
import { Notice } from '@/components/ui/Notice';
import { NumberStepper } from '@/components/ui/NumberStepper';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { TextField } from '@/components/ui/TextField';
import { WorkerCountTag } from '@/components/ui/WorkerCountTag';
import { copyText } from '@/lib/clipboard';
import { formatWhen } from '@/lib/activity';
import { ApiClientError, errorMessage } from '@/services/apiError';
import { personalUrl, publicApi } from '@/services/publicApi';

/*
 * מסכי הקבלן והעובד. ציבוריים — בלי התחברות.
 * הגישה מוגנת בקוד בן 6 ספרות (עם הגבלת ניסיונות בשרת) ובקישור אישי אקראי.
 */

// שמירת הקישור האישי במכשיר — נוחות בלבד; אם האחסון חסום, פשוט לא נשמר.
const savedKey = (slug: string) => `sa-personal:${slug}`;
function readSaved(slug: string): string | null {
  try {
    return localStorage.getItem(savedKey(slug));
  } catch {
    return null;
  }
}
function writeSaved(slug: string, token: string) {
  try {
    localStorage.setItem(savedKey(slug), token);
  } catch {
    /* אחסון חסום — לא נורא */
  }
}

// ── הרשמה לעבודה: /j/:slug ─────────────────────────────────────

type Step = { kind: 'code' } | { kind: 'form'; job: PublicJobInfo; code: string } | { kind: 'done'; job: PublicJobInfo; token: string; type: RegistrantType; count: number; name: string };

export function RegisterPage() {
  const { slug = '' } = useParams();
  const [step, setStep] = useState<Step>({ kind: 'code' });
  const saved = readSaved(slug);

  return (
    <ContractorLayout>
      {step.kind === 'code' && (
        <>
          {saved && (
            <div className="mb-5">
              <Notice tone="info">
                כבר נרשמתם לעבודה הזו מהמכשיר הזה.{' '}
                <Link to={`/c/${saved}`} className="font-semibold underline">
                  לדף האישי שלי
                </Link>
              </Notice>
            </div>
          )}
          <CodeStep slug={slug} onOk={(job, code) => setStep({ kind: 'form', job, code })} />
        </>
      )}
      {step.kind === 'form' && (
        <FormStep
          slug={slug}
          job={step.job}
          code={step.code}
          onDone={(token, type, count, name) => {
            writeSaved(slug, token);
            setStep({ kind: 'done', job: step.job, token, type, count, name });
          }}
        />
      )}
      {step.kind === 'done' && <DoneStep {...step} />}
    </ContractorLayout>
  );
}

function CodeStep({ slug, onOk }: { slug: string; onOk: (job: PublicJobInfo, code: string) => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return setError('יש להזין את 6 הספרות שקיבלתם');
    setBusy(true);
    setError(null);
    try {
      onOk(await publicApi.enter(slug, code), code);
    } catch (err) {
      setError(errorMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <h1 className="text-[24px] font-extrabold">הרשמה לעבודה</h1>
      <p className="mt-1.5 text-[15px] text-ink-2">הזינו את קוד הכניסה בן 6 הספרות שקיבלתם עם הקישור.</p>
      <label htmlFor="code" className="sr-only">
        קוד כניסה
      </label>
      <input
        id="code"
        value={code}
        onChange={(e) => {
          setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
          setError(null);
        }}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={6}
        autoFocus
        placeholder="••••••"
        aria-invalid={error ? true : undefined}
        className={[
          'ltr tabular mt-6 block h-16 w-full rounded-control border-2 bg-surface text-center text-[32px] font-extrabold tracking-[0.5em]',
          'placeholder:text-line-strong focus:border-brand focus:outline-none',
          error ? 'border-danger' : 'border-line-strong',
        ].join(' ')}
      />
      {error && (
        <div className="mt-3">
          <Notice tone="error">{error}</Notice>
        </div>
      )}
      <Button type="submit" block loading={busy} className="mt-5" disabled={code.length !== 6}>
        המשך
      </Button>
    </form>
  );
}

function FormStep({
  slug,
  job,
  code,
  onDone,
}: {
  slug: string;
  job: PublicJobInfo;
  code: string;
  onDone: (token: string, type: RegistrantType, count: number, name: string) => void;
}) {
  const [type, setType] = useState<RegistrantType | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [count, setCount] = useState<number | ''>('');
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!job.registrationOpen) {
    return (
      <>
        <SystemNotice text={job.notice} />
        <PublicJobCard job={job} />
        <JobAnnouncements job={job} />
        <div className="mt-5">
          <Notice tone="error">{job.active ? 'ההרשמה לעבודה הזו סגורה. לפרטים פנו למנהל.' : 'העבודה הזו כבר הסתיימה.'}</Notice>
        </div>
      </>
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!type) return setErrors({ registrantType: 'יש לבחור אם נרשמים כקבלן או כעובד' });
    const v = validateRegistration({ registrantType: type, fullName: name, phone, ...(count === '' ? {} : { workerCount: count }) });
    if (!v.ok) return setErrors(v.errors);
    setBusy(true);
    try {
      const { personalToken } = await publicApi.register(slug, {
        code,
        registrantType: type,
        fullName: name,
        phone,
        ...(type === 'contractor' ? { workerCount: count as number } : {}),
      });
      onDone(personalToken, type, v.value.workerCount, v.value.fullName);
    } catch (err) {
      if (err instanceof ApiClientError && err.fields) setErrors(err.fields);
      setFormError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const phoneHint = phone && !parseIsraeliPhone(phone).ok ? undefined : 'משמש לזיהוי. לא מוצג לאחרים.';

  return (
    <>
      <SystemNotice text={job.notice} />
      <PublicJobCard job={job} />
      <JobAnnouncements job={job} />
      <form onSubmit={submit} noValidate className="mt-6 space-y-5">
        <h1 className="text-[20px] font-extrabold">הפרטים שלי</h1>
        {formError && <Notice tone="error">{formError}</Notice>}

        <fieldset>
          <legend className="mb-2 text-[14px] font-medium text-ink-2">אני נרשם/ת בתור</legend>
          <div className="grid grid-cols-2 gap-3" role="radiogroup">
            <TypeOption selected={type === 'contractor'} onSelect={() => setType('contractor')} title="קבלן" body="מביא/ה עובדים" />
            <TypeOption
              selected={type === 'worker'}
              onSelect={() => {
                setType('worker');
                setErrors((x) => ({ ...x, workerCount: undefined }));
              }}
              title="עובד/ת"
              body="מגיע/ה לבד"
            />
          </div>
          {errors.registrantType && <p className="mt-1.5 text-[13px] text-danger">{errors.registrantType}</p>}
        </fieldset>

        <TextField label="שם מלא" value={name} onChange={(e) => setName(e.target.value)} error={errors.fullName} autoComplete="name" maxLength={LIMITS.maxNameLength} />
        <TextField
          label="מספר טלפון"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="050-1234567"
          error={errors.phone ?? (phone.length >= 9 && !parseIsraeliPhone(phone).ok ? PHONE_ERROR_MESSAGES.invalid_prefix : undefined)}
          {...(phoneHint ? { hint: phoneHint } : {})}
        />

        {type === 'contractor' && (
          <NumberStepper label="כמה עובדים אני מביא/ה" value={count} onChange={setCount} min={1} max={job.maxWorkers ?? LIMITS.maxWorkers} error={errors.workerCount ?? null} />
        )}

        <Button type="submit" block loading={busy}>
          שליחת ההרשמה
        </Button>
      </form>
    </>
  );
}

function TypeOption({ selected, onSelect, title, body }: { selected: boolean; onSelect: () => void; title: string; body: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={[
        'relative min-h-20 rounded-control border-2 px-3 py-3 text-start transition-colors',
        selected ? 'border-brand bg-brand-soft' : 'border-line-strong bg-surface hover:border-ink-3',
      ].join(' ')}
    >
      <span className="block text-[17px] font-bold">{title}</span>
      <span className="block text-[13px] text-ink-2">{body}</span>
      {selected && (
        <span className="absolute end-2 top-2 flex size-6 items-center justify-center rounded-full bg-brand text-white">
          <CheckIcon size={15} />
        </span>
      )}
    </button>
  );
}

function DoneStep({ job, token, type, count, name }: { job: PublicJobInfo; token: string; type: RegistrantType; count: number; name: string }) {
  const link = personalUrl(token);
  const [copied, setCopied] = useState(false);
  const selfMessage = [`${BRAND.name} — הרישום שלי`, job.name, `${REGISTRANT_TYPE_LABELS[type]} · ${count} עובדים`, '', 'הקישור האישי שלי (לשינוי כמות או ביטול):', link].join('\n');

  return (
    <div>
      <div className="flex flex-col items-center text-center">
        <span className="flex size-16 items-center justify-center rounded-full bg-ok-soft text-ok">
          <CheckIcon size={34} />
        </span>
        <h1 className="mt-4 text-[24px] font-extrabold">נרשמת בהצלחה</h1>
        <p className="mt-1 text-[15px] text-ink-2">
          {name}, {type === 'contractor' ? `נרשמת כקבלן עם ${count} עובדים` : 'נרשמת כעובד/ת'} ל{job.name}.
        </p>
      </div>

      <section className="mt-6 rounded-control border-2 border-accent/40 bg-accent-soft p-4">
        <h2 className="text-[16px] font-bold">שמרו את הקישור האישי</h2>
        <p className="mt-1 text-[14px] text-ink-2">דרכו אפשר לבקש שינוי בכמות או לבטל הגעה. זה הקישור היחיד שלכם — אל תעבירו אותו לאחרים.</p>
        <div className="mt-3 flex items-center gap-2 rounded-[8px] bg-surface p-2 ps-3">
          <LinkIcon size={17} className="shrink-0 text-ink-3" />
          <span className="ltr min-w-0 flex-1 truncate text-[13px]">{link}</span>
          <button
            type="button"
            onClick={async () => setCopied(await copyText(link))}
            aria-label="העתקת הקישור"
            className="flex size-10 shrink-0 items-center justify-center rounded-[8px] hover:bg-sunken"
          >
            {copied ? <CheckIcon size={18} className="text-ok" /> : <CopyIcon size={18} />}
          </button>
        </div>
        <a
          href={whatsappShareUrl(selfMessage)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 flex min-h-12 items-center justify-center gap-2 rounded-control bg-[#1f7a4d] px-4 text-[15px] font-semibold text-white"
        >
          <WhatsAppIcon size={18} />
          שליחה לעצמי בוואטסאפ
        </a>
      </section>

      <Link to={`/c/${token}`} className="mt-4 flex min-h-12 items-center justify-center rounded-control border border-line-strong font-semibold hover:bg-sunken">
        לדף האישי שלי
      </Link>
    </div>
  );
}

// ── הדף האישי: /c/:token ───────────────────────────────────────

export function PersonalPage() {
  const { token = '' } = useParams();
  const [view, setView] = useState<{ state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; data: PersonalViewResponse }>({
    state: 'loading',
  });
  const markedRef = useRef(false);
  const reload = () =>
    publicApi
      .personal(token)
      .then((data) => {
        setView({ state: 'ready', data });
        // העדכונים נשארים מסומנים "חדש" בביקור הזה, ונרשמים כנצפו אצל המנהלים
        if (!markedRef.current && data.announcements.some((a) => a.isNew)) {
          markedRef.current = true;
          void publicApi.markSeen(token).catch(() => undefined);
        }
      })
      .catch((err) => setView({ state: 'error', message: errorMessage(err) }));
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <ContractorLayout>
      {view.state === 'loading' && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}
      {view.state === 'error' && (
        <div className="py-6 text-center">
          <h1 className="text-[20px] font-bold">לא ניתן לפתוח את הדף</h1>
          <p className="mt-2 text-[15px] text-ink-2">{view.message}</p>
        </div>
      )}
      {view.state === 'ready' && <PersonalContent token={token} data={view.data} onChanged={reload} />}
    </ContractorLayout>
  );
}

function PersonalContent({ token, data, onChanged }: { token: string; data: PersonalViewResponse; onChanged: () => Promise<unknown> }) {
  const { job, registration: reg, pendingRequest: pending, history } = data;
  const isContractor = reg.registrantType === 'contractor';

  return (
    <div className="space-y-5">
      <div>
        <p className="text-[14px] text-ink-2">שלום,</p>
        <h1 className="text-[24px] font-extrabold">{reg.fullName}</h1>
      </div>

      {data.announcements.length > 0 && <AnnouncementList items={data.announcements} title="עדכונים מהמנהל" />}

      <SystemNotice text={job.notice} />
      <PublicJobCard job={job} />

      <section className="flex items-center justify-between gap-4 rounded-control border border-line p-4">
        <div>
          <div className="text-[13px] text-ink-3">הרישום שלי</div>
          <div className="text-[17px] font-bold">{REGISTRANT_TYPE_LABELS[reg.registrantType]}</div>
          <div className="ltr text-start text-[13px] text-ink-2">{reg.phoneMasked}</div>
        </div>
        <WorkerCountTag count={reg.workerCount} size="lg" />
      </section>

      {pending ? (
        <PendingCard token={token} pending={pending} onChanged={onChanged} />
      ) : !job.active ? (
        <Notice tone="info">העבודה הסתיימה. אין אפשרות לשנות את הרישום.</Notice>
      ) : (
        <RequestForm token={token} isContractor={isContractor} current={reg.workerCount} max={job.maxWorkers ?? LIMITS.maxWorkers} onChanged={onChanged} />
      )}

      <ChatSection token={token} initialUnread={data.chat.unread} closed={data.chat.closed || !job.active} />

      {history.length > 0 && (
        <section>
          <h2 className="mb-2 text-[16px] font-bold">בקשות קודמות</h2>
          <ul className="divide-y divide-line rounded-control border border-line">
            {history.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-3 px-3 py-2.5 text-[14px]">
                <span>{describeRequest(h.previousCount, h.requestedCount)}</span>
                <StatusPill status={h.status} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export function StatusPill({ status }: { status: 'pending' | 'approved' | 'rejected' | 'cancelled' }) {
  const map: Record<typeof status, [string, string]> = {
    pending: ['ממתינה לאישור', 'bg-warn-soft text-warn'],
    approved: ['אושרה', 'bg-ok-soft text-ok'],
    rejected: ['נדחתה', 'bg-danger-soft text-danger'],
    cancelled: ['בוטלה', 'bg-sunken text-ink-2'],
  };
  const [label, cls] = map[status];
  return <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${cls}`}>{label}</span>;
}

function PendingCard({ token, pending, onChanged }: { token: string; pending: NonNullable<PersonalViewResponse['pendingRequest']>; onChanged: () => Promise<unknown> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <section className="rounded-control border-2 border-warn/30 bg-warn-soft p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[16px] font-bold">{describeRequest(pending.previousCount, pending.requestedCount)}</h2>
          <p className="mt-0.5 text-[13px] text-ink-2">נשלחה {formatWhen(new Date(pending.createdAt))} · הכמות לא תשתנה עד שמנהל יאשר</p>
        </div>
        <StatusPill status="pending" />
      </div>
      {pending.note && <p className="mt-2 text-[14px] text-ink-2">"{pending.note}"</p>}
      {error && (
        <div className="mt-3">
          <Notice tone="error">{error}</Notice>
        </div>
      )}
      <Button
        variant="secondary"
        className="mt-3 min-h-10 text-[14px]"
        loading={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await publicApi.cancelRequest(token);
            await onChanged();
          } catch (err) {
            setError(errorMessage(err));
            await onChanged();
          } finally {
            setBusy(false);
          }
        }}
      >
        ביטול הבקשה
      </Button>
    </section>
  );
}

function RequestForm({ token, isContractor, current, max, onChanged }: { token: string; isContractor: boolean; current: number; max: number; onChanged: () => Promise<unknown> }) {
  const [count, setCount] = useState<number | ''>(current);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'change' | 'cancel' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  async function send(requestedCount: number, kind: 'change' | 'cancel') {
    setBusy(kind);
    setError(null);
    try {
      await publicApi.createRequest(token, { requestedCount, note });
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="rounded-control border border-line p-4">
      <h2 className="text-[16px] font-bold">{isContractor ? 'שינוי בכמות' : 'לא אוכל להגיע'}</h2>
      <p className="mt-1 text-[14px] text-ink-2">
        {isContractor ? 'הבקשה נשלחת למנהלים. הכמות תתעדכן רק אחרי אישור.' : 'אפשר לבקש ביטול הגעה. הביטול ייכנס לתוקף אחרי אישור מנהל.'}
      </p>
      {error && (
        <div className="mt-3">
          <Notice tone="error">{error}</Notice>
        </div>
      )}
      <div className="mt-4 space-y-4">
        {isContractor && <NumberStepper label="כמות חדשה" value={count} onChange={setCount} min={1} max={max} />}
        <label className="block">
          <span className="mb-1.5 block text-[14px] font-medium text-ink-2">
            הערה למנהל <span className="font-normal text-ink-3">(לא חובה)</span>
          </span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={LIMITS.maxRequestNoteLength}
            className="block min-h-12 w-full rounded-control border border-line-strong px-3.5 text-[16px] focus:border-brand focus:outline-none"
          />
        </label>
        {isContractor && (
          <Button block loading={busy === 'change'} disabled={count === '' || count === current} onClick={() => count !== '' && send(count, 'change')}>
            שליחת בקשה לשינוי
          </Button>
        )}
        {confirmCancel ? (
          <ConfirmRow
            onYes={() => send(0, 'cancel')}
            onNo={() => setConfirmCancel(false)}
            busy={busy === 'cancel'}
          >
            לבקש ביטול הגעה{isContractor ? ` של כל ${current} העובדים` : ''}?
          </ConfirmRow>
        ) : (
          <Button variant={isContractor ? 'ghost' : 'danger'} block className={isContractor ? 'text-danger hover:bg-danger-soft' : ''} onClick={() => setConfirmCancel(true)}>
            בקשה לביטול הגעה
          </Button>
        )}
      </div>
    </section>
  );
}

function ConfirmRow({ children, onYes, onNo, busy }: { children: ReactNode; onYes: () => void; onNo: () => void; busy: boolean }) {
  return (
    <div className="rounded-control bg-danger-soft p-3">
      <p className="text-[15px] font-semibold text-danger">{children}</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button variant="secondary" onClick={onNo}>
          לא
        </Button>
        <Button variant="danger" loading={busy} onClick={onYes}>
          כן, לבטל
        </Button>
      </div>
    </div>
  );
}

// ── עדכונים ושיחה ──────────────────────────────────────────────

function JobAnnouncements({ job }: { job: PublicJobInfo }) {
  if (!job.announcements?.length) return null;
  return (
    <div className="mt-4">
      <AnnouncementList items={job.announcements} title="עדכון מהמנהל" />
    </div>
  );
}

function AnnouncementList({ items, title }: { items: Array<PublicAnnouncement & { isNew?: boolean }>; title: string }) {
  return (
    <section className="rounded-control border-2 border-accent/30 bg-accent-soft p-4">
      <h2 className="mb-2 flex items-center gap-2 text-[16px] font-bold">
        <MegaphoneIcon size={19} className="text-accent" />
        {title}
      </h2>
      <ul className="space-y-2.5">
        {items.map((a) => {
          const at = new Date(a.updatedAt);
          return (
            <li key={a.id} className="rounded-[8px] bg-surface p-3 shadow-sm">
              {a.isNew && <span className="mb-1 inline-block rounded-full bg-accent px-2 py-0.5 text-[11px] font-bold text-white">חדש</span>}
              <p className="whitespace-pre-line text-[15px] leading-relaxed">{a.text}</p>
              {!Number.isNaN(at.getTime()) && <time className="mt-1 block text-[12px] text-ink-3">{formatWhen(at)}</time>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const CHAT_POLL_MS = 12_000;

/** שיחה פרטית עם המנהלים. נטענת כשפותחים אותה ומתרעננת כל 12 שניות כל עוד היא פתוחה והדף גלוי. */
function ChatSection({ token, initialUnread, closed: initiallyClosed }: { token: string; initialUnread: number; closed: boolean }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(initialUnread);
  const [messages, setMessages] = useState<ChatMessageView[] | null>(null);
  const [closed, setClosed] = useState(initiallyClosed);
  const [error, setError] = useState<string | null>(null);
  const lastAdminId = useRef<string | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const res = await publicApi.chat(token);
      setMessages(res.messages);
      setClosed(res.closed);
      setError(null);
      const lastAdmin = [...res.messages].reverse().find((m) => m.sender === 'admin');
      if (lastAdmin && lastAdmin.id !== lastAdminId.current) {
        lastAdminId.current = lastAdmin.id;
        void publicApi.markChatRead(token).catch(() => undefined);
      }
      setUnread(0);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [token]);

  useEffect(() => {
    if (!open) return;
    void load();
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, CHAT_POLL_MS);
    const onVisible = () => document.visibilityState === 'visible' && void load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [open, load]);

  const lines: ChatLine[] = useMemo(
    () =>
      (messages ?? []).map((m) => ({
        id: m.id,
        mine: m.sender === 'contractor',
        authorName: m.sender === 'admin' ? m.authorName : '',
        text: m.text,
        at: new Date(m.createdAt),
      })),
    [messages],
  );

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex min-h-14 w-full items-center gap-3 rounded-control border border-line px-4 text-start hover:bg-sunken/60"
      >
        <ChatIcon className="text-brand" />
        <span className="flex-1">
          <span className="block text-[16px] font-bold">שיחה עם המנהלים</span>
          <span className="block text-[13px] text-ink-2">{closed ? 'לצפייה בהודעות קודמות' : 'שאלה, עדכון או איחור — כתבו כאן'}</span>
        </span>
        {unread > 0 && <span className="tabular min-w-6 rounded-full bg-accent px-2 text-center text-[13px] font-bold leading-6 text-white">{unread}</span>}
      </button>
    );
  }

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-[16px] font-bold">
          <ChatIcon size={19} className="text-brand" />
          שיחה עם המנהלים
        </h2>
        <button type="button" onClick={() => setOpen(false)} className="text-[14px] font-medium text-ink-2 hover:text-ink">
          סגירה
        </button>
      </div>
      <ChatView
        lines={lines}
        loading={messages === null && !error}
        error={error ? <Notice tone="error">{error}</Notice> : undefined}
        closed={closed}
        closedText="העבודה הסתיימה — השיחה פתוחה לקריאה בלבד."
        emptyText="אפשר לכתוב כאן למנהלים. התשובה תופיע בדף הזה."
        height="h-[50vh]"
        onSend={async (text) => {
          try {
            await publicApi.sendMessage(token, text);
            await load();
            return true;
          } catch (err) {
            toast(errorMessage(err), 'error');
            return false;
          }
        }}
      />
      <p className="mt-2 text-[12px] text-ink-3">ההודעות לא נשלחות לטלפון — חזרו לקישור הזה כדי לראות תשובה.</p>
    </section>
  );
}

/** הודעה קבועה מהגדרות המערכת (או הודעת עצירה זמנית) */
function SystemNotice({ text }: { text?: string | undefined }) {
  if (!text) return null;
  return (
    <div className="mb-4 whitespace-pre-line rounded-control border border-brand/20 bg-brand-soft p-3 text-[15px] font-medium text-brand-strong">{text}</div>
  );
}
